import AccountBalanceWallet from '@material-ui/icons/AccountBalanceWallet'
import AccountTree from '@material-ui/icons/AccountTree'
import ArrowDownward from '@material-ui/icons/ArrowDownward'
import ArrowUpward from '@material-ui/icons/ArrowUpward'
import DonutLarge from '@material-ui/icons/DonutLarge'
import MonetizationOn from '@material-ui/icons/MonetizationOn'
import People from '@material-ui/icons/People'
import PieChart from '@material-ui/icons/PieChart'
import ShowChart from '@material-ui/icons/ShowChart'
import SwapVert from '@material-ui/icons/SwapVert'
import Timeline from '@material-ui/icons/Timeline'
import {ComponentType, ErrorInfo, ReactNode} from 'react'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import {ErrorBoundaryWrapper} from '../../ErrorBoundaryWrapper'
import messages from '../../../assets/messages'
import {PammOfferType} from '../../../graphql/types'
import {OfferNode} from '../types'
import {useKpiPanelState} from '../utils/kpiPanelState'
import {
  computeDownlineFunds,
  computeNetFlow,
  computeParentAumShare,
  countDescendants,
  formatFeePercent,
  formatKpiCount,
  formatKpiMoney,
  formatShare,
  formatTradingInterval,
  formatWinRate,
  getNodeRole,
  NodeRole,
  PLACEHOLDER,
  pnlTone,
  PnlTone,
} from '../utils/kpiGrid'
import {AgentChainBreadcrumb} from './AgentChainBreadcrumb'
import {Panel, PanelBody, PanelHeading} from './BrokereeKpiPanel.styled'
import {ContributionsTable} from './ContributionsTable'
import {DetailRow, KpiDetailList} from './KpiDetailList'
import {KpiPanelError} from './KpiPanelError'
import {KpiPanelSkeleton} from './KpiPanelSkeleton'
import {kpiPanelId} from './KpiPanelContext'
import {KpiTile} from './KpiTile'

type BrokereeKpiPanelProps = {
  node: OfferNode
} & WithNamespaces

type KpiMessage = {i18nKey: string; defaults: string}

const ICON_SIZE = {fontSize: 15}

type TileModel = {
  testId: string
  icon: ReactNode
  label: KpiMessage
  value: string
  sub: ReactNode
  tooltip: KpiMessage
  tone?: PnlTone
}

/** The role name shown in the Ownership section's "Role" row. */
const ROLE_LABEL: Record<NodeRole, KpiMessage> = {
  master: messages.pammRoleMoneyManager,
  agent: messages.pammRoleAgent,
  investor: messages.pammRoleInvestor,
}

/**
 * Sub-line of the MM Trades tile (PT-5815). Three shapes, because the figures are only as specific
 * as the window allows: nothing known at all falls back to the bare period label, a window in which
 * the manager closed nothing drops the win rate rather than printing a misleading 0%, and a window
 * with closed trades shows both.
 *
 * @param mmTrades - The offer's `mmTrades30d`, or null/undefined when the server did not supply it.
 */
const renderMmTradesSub = (mmTrades: PammOfferType['mmTrades30d']): ReactNode => {
  if (mmTrades == null) return <Trans {...messages.pammKpiMmTradesSub} />
  const open = formatKpiCount(mmTrades.open ?? 0)
  return mmTrades.winRate != null ? (
    <Trans {...messages.pammKpiMmTradesSubDetail} values={{winRate: formatWinRate(mmTrades.winRate), open}} />
  ) : (
    <Trans {...messages.pammKpiMmTradesSubOpenOnly} values={{open}} />
  )
}

/**
 * Inline KPI panel for a single offer node (PT-5711, PT-5712). The panel adapts to the node's role
 * so it only ever shows metrics that make sense for it — a Money Manager, an operating agent, and a
 * leaf investor position each get a different tile set, and no tile is ever rendered blank because
 * it does not apply (hidden means absent). Below the tiles it lists the offer's static details and
 * ownership. Every figure is read straight off the node already in memory — the offer's precomputed
 * fields plus a walk of its loaded descendants — so the panel renders with no extra network call and
 * is stable per node id.
 *
 * PF Collected, Node PnL and MM Trades are live 30-day figures sourced from the vendor DB (PT-5784,
 * PT-5809, PT-5815); every tile on the grid is now sourced.
 *
 * @param node - The offer tree node whose KPIs this panel describes.
 */
export const BrokereeKpiPanel = withNamespaces()(
  ({node, t}: BrokereeKpiPanelProps) => {
  // Panel readiness (PT-5716): `ready` today off the in-memory node, but `loading`/`error` are
  // wired now so the real-API production path — and QA's force flag — feed the same UI.
  const {status, retry} = useKpiPanelState(node)

  // Failure state: figures could not be derived (or QA forced it). Render the retry card without
  // touching the offer's fields, since an underivable node has no offer to read.
  if (status === 'error') {
    // Keep the panel's DOM id when the offer is present, so the toggle button's `aria-controls`
    // still resolves in the error state; an underivable node has no id to key on.
    const offerId = node?.offer?.id
    return (
      <Panel
        id={offerId != null ? kpiPanelId(String(offerId)) : undefined}
        role="region"
        aria-label="KPIs"
        data-testid="brokeree-kpi-panel"
      >
        <PanelHeading>
          <Trans {...messages.pammKpiPanelTitle} />
        </PanelHeading>
        <KpiPanelError onRetry={retry} />
      </Panel>
    )
  }

  const {offer, level} = node
  const role = getNodeRole(node)
  const currency = offer.currency ?? 'USD'
  const performanceFeeValue = offer.settings?.performanceFees?.levels?.[0]?.value
  // An investor is charged the fee of the offer they invest into (their parent), not their own.
  const chargedFeeValue = node.parent?.offer.settings?.performanceFees?.levels?.[0]?.value ?? performanceFeeValue
  const downlineFunds = computeDownlineFunds(node)
  const downlineCount = countDescendants(node)

  // Tiles common to every role.
  const totalFundsTile: TileModel = {
    testId: 'kpi-total-funds',
    icon: <AccountBalanceWallet style={ICON_SIZE} />,
    label: messages.pammKpiTotalFundsLabel,
    value: formatKpiMoney(offer.aum, currency),
    sub: <Trans {...messages.pammKpiTotalFundsSub} />,
    tooltip: messages.pammKpiTotalFundsTooltip,
  }
  const perfFeeTile: TileModel = {
    testId: 'kpi-perf-fee',
    icon: <PieChart style={ICON_SIZE} />,
    label: messages.pammKpiPerfFeeLabel,
    value: formatFeePercent(performanceFeeValue),
    sub: <Trans {...messages.pammKpiPerfFeeSub} />,
    tooltip: messages.pammKpiPerfFeeTooltip,
  }
  // Investor variant of the fee tile: the rate the investor is charged, sourced from the parent offer.
  const perfFeeChargedTile: TileModel = {
    ...perfFeeTile,
    value: formatFeePercent(chargedFeeValue),
    tooltip: messages.pammKpiPerfFeeChargedTooltip,
  }
  const depositsTile: TileModel = {
    testId: 'kpi-deposits',
    icon: <ArrowDownward style={ICON_SIZE} />,
    label: messages.pammKpiDepositsLabel,
    value: formatKpiMoney(offer.totalDepositSum, currency),
    sub: <Trans {...messages.pammKpiDepositsSub} />,
    tooltip: messages.pammKpiDepositsTooltip,
  }
  const withdrawalsTile: TileModel = {
    testId: 'kpi-withdrawals',
    icon: <ArrowUpward style={ICON_SIZE} />,
    label: messages.pammKpiWithdrawalsLabel,
    value: formatKpiMoney(offer.lifetimeWithdrawals, currency),
    sub: <Trans {...messages.pammKpiWithdrawalsSub} />,
    tooltip: messages.pammKpiWithdrawalsTooltip,
  }
  // Net Flow = Deposits (LTD) − Withdrawals (LTD), colour-coded by sign so a net outflow reads red
  // (PT-5715). Derived from the very two tiles above it, so QA can add them up on screen and check.
  const netFlow = computeNetFlow(offer.totalDepositSum, offer.lifetimeWithdrawals)
  const netFlowTile: TileModel = {
    testId: 'kpi-net-flow',
    icon: <SwapVert style={ICON_SIZE} />,
    label: messages.pammKpiNetFlowLabel,
    value: formatKpiMoney(netFlow, currency),
    sub: <Trans {...messages.pammKpiNetFlowSub} />,
    tooltip: messages.pammKpiNetFlowTooltip,
    tone: pnlTone(netFlow),
  }

  // Downline-only tiles — meaningful for a Money Manager or an operating agent, hidden for a leaf
  // investor node that has no one beneath it.
  const downlineFundsTile: TileModel = {
    testId: 'kpi-downline-funds',
    icon: <AccountTree style={ICON_SIZE} />,
    label: messages.pammKpiDownlineFundsLabel,
    value: formatKpiMoney(downlineFunds, currency),
    sub: <Trans {...messages.pammKpiDownlineFundsSub} values={{count: downlineCount}} />,
    tooltip: messages.pammKpiDownlineFundsTooltip,
  }
  const clientsTile: TileModel = {
    testId: 'kpi-clients',
    icon: <People style={ICON_SIZE} />,
    label: messages.pammKpiClientsLabel,
    value: formatKpiCount(offer.totalInvestments),
    sub: <Trans {...messages.pammKpiClientsSub} />,
    tooltip: messages.pammKpiClientsTooltip,
  }

  // How the Money Manager themselves traded over the window (PT-5815). The headline figure is the
  // trades they closed; the win rate and the still-open count ride along in the sub-line.
  const mmTradesTile: TileModel = {
    testId: 'kpi-mm-trades',
    icon: <Timeline style={ICON_SIZE} />,
    label: messages.pammKpiMmTradesLabel,
    value: formatKpiCount(offer.mmTrades30d?.closed),
    sub: renderMmTradesSub(offer.mmTrades30d),
    tooltip: messages.pammKpiMmTradesTooltip,
  }
  // Realised 30-day P/L (PT-5809). Money rather than a percentage — the figure the server sources is
  // an amount and there is no per-node cost basis to render a return against — so it is formatted and
  // toned exactly like Net Flow above, the grid's other signed money tile.
  const nodePnlTile: TileModel = {
    testId: 'kpi-node-pnl',
    icon: <ShowChart style={ICON_SIZE} />,
    label: messages.pammKpiNodePnlLabel,
    value: formatKpiMoney(offer.nodePnl30d, currency),
    sub: <Trans {...messages.pammKpiNodePnlSub} />,
    tooltip: messages.pammKpiNodePnlTooltip,
    tone: pnlTone(offer.nodePnl30d),
  }
  const pfCollectedTile: TileModel = {
    testId: 'kpi-pf-collected',
    icon: <MonetizationOn style={ICON_SIZE} />,
    label: messages.pammKpiPfCollectedLabel,
    value: formatKpiMoney(offer.pfCollected30d, currency),
    sub: <Trans {...messages.pammKpiPfCollectedSub} />,
    tooltip: messages.pammKpiPfCollectedTooltip,
  }

  // Investor-only tile: this node's funds as a share of the parent offer's AUM.
  const yourShareTile: TileModel = {
    testId: 'kpi-your-share',
    icon: <DonutLarge style={ICON_SIZE} />,
    label: messages.pammKpiYourShareLabel,
    value: formatShare(computeParentAumShare(node)),
    sub: <Trans {...messages.pammKpiYourShareSub} />,
    tooltip: messages.pammKpiYourShareTooltip,
  }

  const tilesByRole: Record<NodeRole, TileModel[]> = {
    master: [
      totalFundsTile,
      downlineFundsTile,
      clientsTile,
      perfFeeTile,
      depositsTile,
      withdrawalsTile,
      netFlowTile,
      mmTradesTile,
      // The Money Manager's own offer is where the trading actually happens, so its realised P/L is
      // the most meaningful node PnL in the tree (PT-5809).
      nodePnlTile,
      // A Money Manager's top offer is where the combined "AllFees" is actually collected, so the
      // PF Collected figure is most meaningful here (PT-5784).
      pfCollectedTile,
    ],
    agent: [
      totalFundsTile,
      downlineFundsTile,
      clientsTile,
      perfFeeTile,
      depositsTile,
      withdrawalsTile,
      netFlowTile,
      nodePnlTile,
      pfCollectedTile,
    ],
    investor: [totalFundsTile, yourShareTile, perfFeeChargedTile, depositsTile, withdrawalsTile, netFlowTile],
  }
  const tiles = tilesByRole[role]

  const offerDetailRows: DetailRow[] = [
    {id: 'detail-offer-id', label: messages.pammKpiDetailOfferId, value: offer.id != null ? String(offer.id) : PLACEHOLDER},
    {id: 'detail-name', label: messages.pammKpiDetailName, value: offer.name || PLACEHOLDER},
    {
      id: 'detail-trading-interval',
      label: messages.pammKpiDetailTradingInterval,
      value: formatTradingInterval(offer.settings?.tradingInterval, t),
    },
    {id: 'detail-min-deposit', label: messages.pammKpiDetailMinDeposit, value: formatKpiMoney(offer.settings?.minDeposit, currency)},
    {
      id: 'detail-min-withdrawal',
      label: messages.pammKpiDetailMinWithdrawal,
      value: formatKpiMoney(offer.settings?.minWithdrawal, currency),
    },
    {
      id: 'detail-min-initial',
      label: messages.pammKpiDetailMinInitial,
      value: formatKpiMoney(offer.settings?.minInitialInvestment, currency),
    },
  ]

  const ownershipRows: DetailRow[] = [
    {id: 'detail-owner', label: messages.pammKpiDetailOwner, value: offer.managerName || PLACEHOLDER},
    {
      id: 'detail-login-id',
      label: messages.pammKpiDetailLoginId,
      value: offer.skygroundAccountId ?? offer.managerId ?? PLACEHOLDER,
    },
    {id: 'detail-role', label: messages.pammKpiDetailRole, value: <Trans {...ROLE_LABEL[role]} />},
    {id: 'detail-tree-level', label: messages.pammKpiDetailTreeLevel, value: String(level)},
    // Investor positions also show how much of the parent they hold, and their own stake when it exists.
    ...(role === 'investor'
      ? [
          // "My Investment (if any)" — omit the row entirely when the node has no funds of its own.
          ...(offer.aum != null
            ? [{id: 'detail-my-investment', label: messages.pammKpiDetailMyInvestment, value: formatKpiMoney(offer.aum, currency)}]
            : []),
          {
            id: 'detail-share-of-parent',
            label: messages.pammKpiDetailShareOfParent,
            value: formatShare(computeParentAumShare(node)),
          },
        ]
      : []),
  ]

  return (
    <Panel
      id={kpiPanelId(String(offer.id))}
      role="region"
      aria-label={`${offer.name} KPIs`}
      data-testid="brokeree-kpi-panel"
    >
      <PanelHeading>
        <Trans {...messages.pammKpiPanelTitle} />
      </PanelHeading>
      {/* An uncaught error while rendering a tile must never white-screen the card: the boundary
          degrades to the same failure card as an underivable node, and reports to Sentry (PT-5716). */}
      <ErrorBoundaryWrapper FallbackComponent={KpiPanelErrorFallback}>
        {status === 'loading' ? (
          // Skeleton grid sized to this node's live tile count, so there is no layout jump when the
          // real tiles arrive (PT-5716).
          <KpiPanelSkeleton count={tiles.length} />
        ) : (
          <>
            {/* Agent Chain breadcrumb (PT-5713): Root → … → this node, so an agent can see who sits
                upstream of them and jump to any owned ancestor. */}
            <AgentChainBreadcrumb node={node} />
            <PanelBody>
              {tiles.map((tile) => (
                <KpiTile
                  key={tile.testId}
                  testId={tile.testId}
                  icon={tile.icon}
                  label={tile.label}
                  value={tile.value}
                  sub={tile.sub}
                  tooltip={tile.tooltip}
                  tone={tile.tone}
                />
              ))}
            </PanelBody>
            {/* Funds by Direct Agent/Investor (PT-5714): who sits directly beneath this node, largest
                contributor first. Shown for any node that can have a downline — an investor leaf never
                distributes further, so it gets no such table. */}
            {role !== 'investor' && <ContributionsTable node={node} />}
            <KpiDetailList
              heading={messages.pammKpiOfferDetailsHeading}
              rows={offerDetailRows}
              testId="kpi-offer-details"
            />
            <KpiDetailList heading={messages.pammKpiOwnerHeading} rows={ownershipRows} testId="kpi-ownership" />
          </>
        )}
      </ErrorBoundaryWrapper>
    </Panel>
  )
  },
)

/**
 * Fallback the panel's {@link ErrorBoundaryWrapper} renders when a tile throws mid-render: the same
 * failure card as an underivable node, wired to the boundary's reset so the user can retry (PT-5716).
 * Takes the boundary's full fallback props; only `clearError` (the reset) is needed here.
 */
const KpiPanelErrorFallback: ComponentType<{error: Error; info: ErrorInfo; clearError: () => void}> = ({
  clearError,
}) => <KpiPanelError onRetry={clearError} />
